import { inject, Injectable } from '@angular/core';
import { CrmObjectDisplayService, CrmObjectService } from '../../../shared-services';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
  ObjectType,
} from '../../../tokens-and-interfaces';
import { Observable, catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { CrmObjectInterface } from '@vendasta/crm';
import { PAGE_ROUTES } from '../../../constants';

@Injectable()
export class AssociatedCrmObjectService {
  private readonly config: CrmDependencies = inject(CrmInjectionToken);
  private readonly multiLocation: CrmMultiLocationDependencies = inject(CrmMultilocationInjectionToken);
  private readonly crmObjectService = inject(CrmObjectService);
  private readonly objectDisplayService = inject(CrmObjectDisplayService);

  public getCrmObjectName(namespace?: string, objectType?: ObjectType, objectId?: string): Observable<string> {
    if (!namespace || !objectType || !objectId) {
      return of('');
    }
    return this.getCrmObject(namespace, objectType, objectId).pipe(
      map((crmObject) => {
        if (crmObject) {
          return this.objectDisplayService.getObjectDisplayName(objectType, crmObject);
        }
        return '';
      }),
    );
  }

  private getCrmObject(
    namespace: string,
    objectType: ObjectType,
    objectId: string,
  ): Observable<CrmObjectInterface | undefined> {
    return this.crmObjectService.getMultiObject(objectType, [objectId], [], namespace).pipe(
      map((response) => response?.crmObjects?.[0]),
      catchError(() => of(undefined)),
    );
  }

  public getCrmObjectUrl(namespace?: string, objectType?: ObjectType, objectId?: string): Observable<string> {
    if (!namespace || !objectType || !objectId) {
      return of('');
    }
    const isMultiLocation$ = this.multiLocation.isMultiLocation$ ?? of(false);
    const routePrefix$ = isMultiLocation$.pipe(
      switchMap((isMultiLocation) => {
        return isMultiLocation ? this.multiLocation.routePrefix$ : this.config.routePrefix$;
      }),
    );
    return combineLatest([routePrefix$, isMultiLocation$]).pipe(
      map(([pathRoot, isMultiLocation]) => {
        let pathTemplate = '';
        switch (objectType) {
          case 'Company':
            if (isMultiLocation) {
              return '';
            }
            pathTemplate = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
            break;
          case 'Contact':
            if (isMultiLocation) {
              pathTemplate = `${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.ROOT}/${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.SUBROUTES.PROFILE}`;
              break;
            }
            pathTemplate = `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
            break;
          default:
            return '';
        }

        return `${pathRoot}/${pathTemplate}`.replace(':crmObjectId', objectId).replace(':namespace', namespace);
      }),
    );
  }
}
